import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

/**
 * Badge — compact inline label.
 *
 * Variants tuned for the brand palette:
 *   - default / muted / brand / success / warning / error / info
 *
 * For order/line statuses, prefer <StatusBadge> which knows the state-machine
 * mapping (Phase 1 §07).
 */

const badgeVariants = cva(
  cn(
    'inline-flex items-center gap-1.5 rounded-full',
    'px-2 py-0.5 text-xs font-medium',
    'border whitespace-nowrap select-none',
  ),
  {
    variants: {
      variant: {
        default: 'bg-sand-100 text-text-secondary border-sand-200',
        muted: 'bg-sand-50 text-text-muted border-sand-100',
        brand: 'bg-brand-50 text-brand-700 border-brand-100',
        success: 'bg-success-50 text-success-700 border-success-100',
        warning: 'bg-warning-50 text-warning-700 border-warning-100',
        error: 'bg-error-50 text-error-700 border-error-100',
        info: 'bg-info-50 text-info-700 border-info-100',
        outline: 'bg-transparent text-text-secondary border-border-default',
      },
      size: {
        sm: 'text-[11px] px-1.5 py-0',
        md: 'text-xs px-2 py-0.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

export function Badge({ className, variant, size, dot, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {dot ? (
        <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />
      ) : null}
      {children}
    </span>
  );
}
